import { useEffect, useState, useCallback } from "react";
import { DownloadSimple, X, ArrowClockwise, AppleLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

const DISMISS_KEY = "nowcast.pwa.installDismissedAt";
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60_000; // 7 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return (
    /iPhone|iPad|iPod/i.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

/**
 * PWA install prompt + update notifier.
 *
 * Android/Desktop Chromium: captures the `beforeinstallprompt` event and
 * exposes an "Install app" pill. Tapping the pill triggers the native
 * install sheet.
 *
 * iOS (Safari has no beforeinstallprompt): shows a compact hint with the
 * "Tap ⎋ → Add to Home Screen" steps since that's the only way to install
 * on iOS Safari.
 *
 * Update flow: when `nowcast:sw-update` event fires (from registerSW.js),
 * surfaces a Sonner toast with a Reload action.
 */
export default function InstallPwaPrompt() {
  const [deferredEvt, setDeferredEvt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());

  // Capture Chromium install prompt
  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredEvt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredEvt(null);
      toast.success("Nowcast installed — launch it from your home screen");
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // SW-update toast
  useEffect(() => {
    const onUpdate = () => {
      toast(
        (t) => (
          <div className="flex items-center gap-3">
            <ArrowClockwise size={16} weight="bold" className="text-amber-300" />
            <span className="text-sm">New version available</span>
            <button
              type="button"
              data-testid="pwa-reload-button"
              onClick={() => {
                // Ask the waiting SW to take over; reload happens on controllerchange
                navigator.serviceWorker
                  .getRegistration()
                  .then((reg) => reg?.waiting?.postMessage({ type: "SKIP_WAITING" }));
                toast.dismiss(t);
              }}
              className="ml-2 px-3 py-1 rounded-full bg-amber-400 text-[#0b0f19] text-xs font-semibold"
            >
              Reload
            </button>
          </div>
        ),
        { duration: Infinity },
      );
    };
    window.addEventListener("nowcast:sw-update", onUpdate);
    return () => window.removeEventListener("nowcast:sw-update", onUpdate);
  }, []);

  // iOS auto-hint once (after 4s of use, only once per 7 days)
  useEffect(() => {
    if (installed || !isIos()) return;
    const last = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (Date.now() - last < DISMISS_COOLDOWN_MS) return;
    const id = setTimeout(() => setShowIosHint(true), 4000);
    return () => clearTimeout(id);
  }, [installed]);

  const installNow = useCallback(async () => {
    if (!deferredEvt) return;
    deferredEvt.prompt();
    const choice = await deferredEvt.userChoice;
    if (choice.outcome === "accepted") {
      toast.success("Installing…");
    } else {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
    setDeferredEvt(null);
  }, [deferredEvt]);

  const dismissIos = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShowIosHint(false);
  };

  if (installed) return null;

  // Chromium install button (integrated in the header area)
  if (deferredEvt) {
    return (
      <button
        type="button"
        onClick={installNow}
        data-testid="install-pwa-button"
        title="Install Nowcast on your device"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                   border border-amber-500/40 bg-amber-400/15 text-amber-700
                   hover:bg-amber-400/25 hover:border-amber-500/60
                   transition-all text-[11px] font-semibold tracking-wide"
      >
        <DownloadSimple size={13} weight="duotone" />
        <span>Install app</span>
      </button>
    );
  }

  // iOS-only hint (Safari doesn't fire beforeinstallprompt)
  if (showIosHint) {
    return (
      <div
        className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-50
                   rounded-2xl border border-amber-300/30
                   bg-[#0d1322]/95 backdrop-blur-xl shadow-2xl shadow-black/40
                   p-4"
        data-testid="install-pwa-ios-hint"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 shrink-0 rounded-xl bg-amber-400/15 ring-1 ring-amber-300/40 flex items-center justify-center">
            <AppleLogo size={16} weight="duotone" className="text-amber-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-white">
              Add Nowcast to your Home Screen
            </div>
            <p className="mt-1 text-[12px] text-slate-300/85 leading-snug">
              Tap the <span className="inline-block px-1.5 py-0.5 rounded bg-white/10 text-slate-100">Share ⎋</span> button in Safari, then{" "}
              <span className="text-amber-200 font-medium">Add to Home Screen</span>.
              Launch it fullscreen anytime.
            </p>
          </div>
          <button
            type="button"
            onClick={dismissIos}
            aria-label="Dismiss install hint"
            data-testid="install-pwa-dismiss"
            className="shrink-0 w-7 h-7 rounded-lg border border-white/10 hover:bg-white/5 flex items-center justify-center text-slate-400 hover:text-white"
          >
            <X size={12} weight="bold" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
